import {API_URL, GLOBAL_ADMIN_ID} from "../common/struct/urlManager";
import {
  del,
  get,
  getPaginated,
  post, postWithoutToken,
  put,
  RequestFilter,
  RequestOrder
} from "../common/methods/ApiService";
import {TEmailTemplate, TEmailTemplateCreate, TEmailTemplateComponent, TEmailTemplateComponentCreate} from "../interfaces/email";
import {TOperationParticipantDeleted} from "../interfaces/operationParticipant";
import {TAdmin} from "../interfaces/admin";
import {TAddressCreate} from "../interfaces/address";
import {GridColumnVisibilityModel} from "@mui/x-data-grid";

export async function getEmailTemplate(id: string): Promise<TEmailTemplate|null> {
  return get(`${API_URL.EMAIL_TEMPLATES}/${id}`);
}

export async function createEmailTemplate(emailTemplate: TEmailTemplateCreate): Promise<TEmailTemplate> {
  return post(API_URL.EMAIL_TEMPLATES, emailTemplate);
}

export async function updateEmailTemplate(id: string, emailTemplate: TEmailTemplateCreate): Promise<TEmailTemplate> {
  return put(API_URL.EMAIL_TEMPLATES, id, emailTemplate);
}

export async function deleteEmailTemplate(id: string): Promise<void> {
  return del(`${API_URL.EMAIL_TEMPLATES}`, id);
}

export async function createEmailTemplateComponent(data: TEmailTemplateComponentCreate): Promise<TEmailTemplateComponent> {
  return post(API_URL.EMAIL_TEMPLATE_COMPONENTS, data);
}

export async function updateEmailTemplateComponent(id: string, data: TEmailTemplateComponentCreate): Promise<TEmailTemplateComponent> {
  return put(API_URL.EMAIL_TEMPLATE_COMPONENTS, id, data);
}

export async function deleteEmailTemplateComponent(id: string): Promise<void> {
  return del(API_URL.EMAIL_TEMPLATE_COMPONENTS, id);
}

export async function getAllEmailTemplateTypes(): Promise<string[]> {
  return get(`${API_URL.EMAIL_TEMPLATES}/types`);
}

export async function getOperationEmailTemplateByType(operationId: string, type: string): Promise<TEmailTemplate> {
  return get(`${API_URL.OPERATION}/${operationId}/email-templates?type=${type}`);
}

export async function getCompanyEmailTemplates(operationId: string): Promise<TEmailTemplate[]> {
  return get(`${API_URL.OPERATION}/${operationId}/company-email-templates`);
}

export async function getCustomTemplatesForOperation(operationId: string): Promise<TEmailTemplate[]> {
  return get(`${API_URL.EMAIL_TEMPLATES}?templateType=CUSTOM_TEMPLATE&operation=${operationId}&pagination=false`);
}

export async function getEmailTemplates(page: number, itemsPerPage: number, search: string, order: RequestOrder, filters: RequestFilter[]): Promise<{totalItems: number, items: TEmailTemplate[]}> {
  return getPaginated(API_URL.EMAIL_TEMPLATES, page, itemsPerPage, search, order, filters);
}

export async function getArchivedEmailTemplates(page: number, itemsPerPage: number, search: string, order: RequestOrder, filters: RequestFilter[]): Promise<{totalItems: number, items: TEmailTemplate[]}> {
  return getPaginated(`${API_URL.EMAIL_TEMPLATES}/archived`, page, itemsPerPage, search, order, filters);
}

export async function restoreEmailTemplate(id: string) : Promise<void> {
  return post(`${API_URL.EMAIL_TEMPLATES}/${id}/restore`);
}

export async function resetInvitationEmailTemplate(id: string): Promise<void> {
  return post(`${API_URL.EMAIL_TEMPLATES}/${id}/reset-invitation`);
}

export async function getActionsHistory(page: number, itemsPerPage: number, search: string, order: RequestOrder, filters: RequestFilter[]): Promise<{totalItems: number, items: TOperationParticipantDeleted[]}> {
  return getPaginated(API_URL.LOGGING_ENTRIES, page, itemsPerPage, search, order, filters);
}

export async function validateEmail(id: string, token: string): Promise<TAdmin> {
  return postWithoutToken(API_URL.VALIDATE_EMAIL.replace(":" + GLOBAL_ADMIN_ID, id), {validationToken: token});
}

export async function sendValidationEmail(id: string): Promise<TAdmin> {
  return post(API_URL.SEND_VALIDATION_EMAIL.replace(":" + GLOBAL_ADMIN_ID, id));
}

export async function updateAdminColumnOrder(id: string, participantColumnsOrder: string[]): Promise<void> {  
  return put(API_URL.ADMIN, id, {participantColumnsOrder: participantColumnsOrder});
}

export async function updateAdminColumnSatus(id: string, participantColumnsStatus: GridColumnVisibilityModel): Promise<void> {
  const jsonStringify = Object.assign({}, participantColumnsStatus);  
  
  return put(API_URL.ADMIN, id, {participantColumnsStatus: jsonStringify});
}

export async function getAdmins(): Promise<TAdmin[]> {
  return get(API_URL.ADMIN);
}

export async function deleteAdmin(adminId: string): Promise<void> {
  return del(API_URL.ADMIN, adminId);
}

export async function updateAdminInvoiceAddress(adminId: string, address: TAddressCreate): Promise<TAdmin> {
  return put(API_URL.ADMIN, adminId, {invoiceAddress: address});
}

export async function updateAdminCompany(adminId: string, companyIri: string): Promise<TAdmin> {
  return put(API_URL.ADMIN, adminId+'/update-company', {company: companyIri});
}

export function updateEmailTemplatePeriodicity(id: string, periodicity: number): Promise<any> {
  return post(`${API_URL.EMAIL_TEMPLATES}/${id}/periodicity`, {periodicity: periodicity});
}
export async function sendTestEmailForTemplate(id: string, email?: string, participantId?: string): Promise<void> {
  return post(`${API_URL.EMAIL_TEMPLATES}/${id}/send-test-email`, {email: email ?? null, participantId: participantId ?? null});
}

export async function getEmailTemplatePreview(id: string, participantId?: string, overrides?: {buttonLabel?: string; content?: string}): Promise<{html: string}> {
  return post(`${API_URL.EMAIL_TEMPLATES}/${id}/preview`, {participantId: participantId ?? null, ...(overrides ?? {})});
}

export async function sendPreviewEmailForTemplate(id: string, email: string, participantId?: string): Promise<void> {
  return post(`${API_URL.EMAIL_TEMPLATES}/${id}/send-preview-email`, {email, participantId: participantId ?? null});
}
export async function getEmailTemplateShareLink(id: string, participantId?: string, regenerate = false): Promise<{url: string; expiresAt: string; participantId: string | null}> {
  return post(`${API_URL.EMAIL_TEMPLATES}/${id}/share-link`, {participantId: participantId ?? null, regenerate});
}